import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Loader2 } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/pulaar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, total, open, setOpen, updateQty, removeItem } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { t } = useTranslation();

  const onCheckout = () => {
    if (items.length === 0) return;
    // single-line cart for now: take the first item — multi-line checkout TBD
    const it = items[0];
    setOpen(false);
    // Navigate to embedded checkout page; URL params carry the cart context
    const qs = new URLSearchParams({
      slug: it.slug,
      qty: String(it.qty),
    });
    if (email) qs.set("email", email);
    if (name) qs.set("name", name);
    window.location.href = `/checkout?${qs.toString()}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/40 z-[70]"
            data-testid="cart-overlay"
          />
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[71] w-full sm:w-[440px] bg-[#FBF8EF] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between h-16 px-6 border-b border-[#E5DFD3]">
              <p className="text-[11px] tracking-[0.32em] uppercase text-[#121212]">
                {t("cart.title")} ({items.length})
              </p>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-btn"
                aria-label={t("specialOrder.close")}
                className="text-[#121212] p-2 -mr-2"
              >
                <X size={20} strokeWidth={1.2} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto" data-lenis-prevent>
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center px-8 text-center">
                  <p className="font-serif italic text-2xl text-[#121212]">
                    {t("cart.empty")}
                  </p>
                  <p className="mt-4 text-sm text-[#4A4641] max-w-xs">
                    {t("cart.emptySub", { defaultValue: "Choisissez une pièce du vestiaire ou écrivez-nous pour une commande personnalisée." })}
                  </p>
                  <Link
                    to="/vestiaire"
                    onClick={() => setOpen(false)}
                    data-testid="cart-empty-vestiaire-link"
                    className="mt-10 inline-flex items-center h-12 px-8 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase"
                  >
                    {t("cart.discoverCta")}
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-[#E5DFD3]">
                  {items.map((it) => (
                    <li
                      key={it.key}
                      data-testid={`cart-item-${it.slug}`}
                      className="flex gap-4 p-6"
                    >
                      <div className="w-20 h-24 overflow-hidden bg-[#F3EFE7] shrink-0">
                        <img
                          src={it.image}
                          alt={it.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-serif text-lg text-[#121212] leading-tight">
                              {it.name}
                            </p>
                            <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-[#4A4641]">
                              {t("cart.sizeUnique")}
                            </p>
                          </div>
                          <button
                            onClick={() => removeItem(it.key)}
                            className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] hover:underline"
                            data-testid={`cart-remove-${it.slug}`}
                          >
                            {t("cart.remove")}
                          </button>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="inline-flex items-center border border-[#E5DFD3]">
                            <button
                              onClick={() => updateQty(it.key, it.qty - 1)}
                              data-testid={`cart-decrement-${it.slug}`}
                              className="w-8 h-8 flex items-center justify-center text-[#121212] hover:bg-[#F3EFE7]"
                              aria-label="Réduire"
                            >
                              <Minus size={12} strokeWidth={1.2} />
                            </button>
                            <span className="w-8 text-center text-sm">{it.qty}</span>
                            <button
                              onClick={() => updateQty(it.key, it.qty + 1)}
                              data-testid={`cart-increment-${it.slug}`}
                              className="w-8 h-8 flex items-center justify-center text-[#121212] hover:bg-[#F3EFE7]"
                              aria-label="Augmenter"
                            >
                              <Plus size={12} strokeWidth={1.2} />
                            </button>
                          </div>
                          <p className="font-serif text-base text-[#121212]">
                            {formatPrice(it.price * it.qty)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#E5DFD3] p-6 space-y-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] tracking-[0.28em] uppercase text-[#4A4641]">
                    {t("cart.summary")}
                  </span>
                  <span
                    className="font-serif text-2xl text-[#121212]"
                    data-testid="cart-total"
                  >
                    {formatPrice(total)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder={t("contact.name")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="cart-name"
                    className="h-11 bg-transparent border border-[#E5DFD3] px-3 text-sm focus:border-[#121212] outline-none"
                  />
                  <input
                    type="email"
                    placeholder={t("contact.email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="cart-email"
                    className="h-11 bg-transparent border border-[#E5DFD3] px-3 text-sm focus:border-[#121212] outline-none"
                  />
                </div>

                <p className="text-[11px] text-[#4A4641] leading-relaxed">
                  {t("cart.note")}
                </p>
                {error && (
                  <p className="text-sm text-[#A33A2A]" data-testid="cart-error">{error}</p>
                )}
                {items.length > 1 && (
                  <p className="text-[11px] text-[#4A4641]">
                    Plusieurs pièces : seule la première est traitée par paiement direct.
                    Pour grouper, écrivez-nous via Contact.
                  </p>
                )}
                <button
                  onClick={onCheckout}
                  disabled={loading}
                  data-testid="cart-checkout-btn"
                  className="w-full h-12 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase hover:bg-[#5C4033] transition-colors duration-500 disabled:opacity-60"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 size={14} className="animate-spin" /> {t("checkout.paymentSubtitle")}
                    </span>
                  ) : (
                    t("cart.checkout")
                  )}
                </button>
                <Link
                  to="/a-propos#contact"
                  onClick={() => setOpen(false)}
                  className="block text-center text-[11px] tracking-[0.28em] uppercase text-[#5C4033] link-underline pt-1"
                  data-testid="cart-contact-link"
                >
                  {t("contact.title")}
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
